import React, { useState, useEffect } from "react";
import clsx from "clsx";
import {
  Box,
  Card,
  Grid,
  makeStyles,
  FormControl,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Button,
} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import { NavLink as RouterLink } from "react-router-dom";
import { useParams } from "react-router";
import { useForm, Controller } from "react-hook-form";
import {
  apiEndpoints,
  apiPostRequest,
  apiGetByIdRequest,
} from "src/utils/api/api";
import { useNavigate } from "react-router-dom";
import actions from "src/duck/global/actions";
import { connect } from "react-redux";
import { kmMessages } from "src/constant/Messages";
import { fillMissingTags } from "src/utils/utils";
import KmFilteredDocumentsTable from "src/components/document/KmFilteredDocumentsTable";
import DocumentStructNameSelectOnly from "src/components/document/DocumentStructNameSelectOnly";
import DocumentStructNameReadOnly from "src/components/document/DocumentStructNameReadOnly";

const nameMaxLength = 150;
const descriptionMaxLength = 500;

const formValidation = {
  required: kmMessages.error.form_validation.field_required,
  maxLength: `${kmMessages.error.form_validation.field_maxlengthnum}`,
};

const useStyles = makeStyles((theme) => ({
  root: {
    "& > *": {
      margin: theme.spacing(2),
    },
  },
  formcontrol: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  paddingbottom: {
    paddingBottom: theme.spacing(1),
  },
  paddingtop: {
    paddingTop: theme.spacing(2),
  },
  button: {
    margin: theme.spacing(1),
  },
}));

const DocumentStructRowName = (row, customHandlers, ...rest) => {
  return (
    <DocumentStructNameSelectOnly
      document={row}
      openDocumentHandler={customHandlers.handleOpenDocument}
    />
  );
};

const DocumentStructRowNameToMove = (row, customHandlers, ...rest) => {
  return <DocumentStructNameReadOnly document={row} />;
};

const StatusCell = (row, customHandlers, ...rest) => {
  const dataLabels = { draft: "Wersja robocza", published: "Opublikowany" };
  return (
    <>
      <Typography variant="body2">{dataLabels[row.status]}</Typography>
      <Typography variant="caption">
        {row.publicAccess ? "(publiczny)" : "(po zalogowaniu)"}
      </Typography>
    </>
  );
};

const MoveForm = ({
  documentData,
  setLogoutUser,
  enableGlobalLoader,
  disableGlobalLoader,
  displaySnackbar,
  editMode,
  ...rest
}) => {
  const classes = useStyles();
  const { parentFolderId } = useParams();
  const navigate = useNavigate();
  const [folderId, setFolderId] = useState("");
  const [documents, setDocuments] = useState([]);
  const [levelDetails, setLevelDetails] = useState({});
  const [selectedDocumentIds, setSelectedDocumentIds] = useState([]);
  const [firstStatus, setFirstStatus] = useState(null);
  const [firstPublicAccess, setFirstPublicAccess] = useState(null);
  const [childMoveAvailable, setChildMoveAvailable] = useState(true);

  const { handleSubmit, setValue, control, errors } = useForm();

  const handleOpenDocument = (event, documentId) => {
    event.preventDefault();
    setFolderId(documentId);
  };

  const headCellsToMove = [
    {
      id: "name",
      numeric: false,
      sortable: true,
      fieldName: "id",
      main: false,
      label: "Nazwa",
      component: DocumentStructRowNameToMove,
    },
    {
      id: "status",
      numeric: false,
      disablePadding: false,
      fieldName: "status",
      sortable: true,
      main: false,
      label: "Status",
      component: StatusCell,
      width: "12%",
    },
  ];

  const headCells = [
    {
      id: "name",
      numeric: false,
      sortable: true,
      fieldName: "id",
      main: false,
      label: "Nazwa",
      customHandlers: { handleOpenDocument },
      component: DocumentStructRowName,
    },
    {
      id: "status",
      numeric: false,
      disablePadding: false,
      fieldName: "status",
      sortable: true,
      main: false,
      label: "Status",
      component: StatusCell,
      width: "12%",
    },
  ];

  const checkAllMovedTheSame = () => {
    let firstStatus = documentData.moveSelectedDocuments[0].status;
    let firstPublicAccess = documentData.moveSelectedDocuments[0].publicAccess;

    setFirstStatus(firstStatus);
    setFirstPublicAccess(firstPublicAccess);

    return documentData.moveSelectedDocuments.every(
      (doc) =>
        doc.status === firstStatus && doc.publicAccess === firstPublicAccess
    );
  };

  const filterDocumentsWithMoved = (documents) => {
    let nodeIds = getSelectedToMoveIds();

    return documents.filter((doc) => {
      return !nodeIds.some((id) => doc.id === id);
    });
  };

  useEffect(() => {
    if (documentData.moveSelectedDocuments?.length > 0) {
      // setMoveNodeIds(getSelectedToMoveIds());

      let allTheSame = checkAllMovedTheSame();

      if (allTheSame) {
        apiGetByIdRequest(
          apiEndpoints.getDocuments.path,
          folderId ? folderId : "",
          true
        )
          .then((data) => {
            setDocuments(filterDocumentsWithMoved(data.documentStructNodes));

            const { documentStructNodes, ...other } = data;

            setLevelDetails(other);
          })
          .catch((error) => {});
      } else {
        displaySnackbar({
          isOpen: true,
          severity: "error",
          autoHideDuration: 3000,
          message: "Nie wszystkie katalogi/dokumenty mają ten sam status",
        });
        navigate(
          parentFolderId ? `/dokumenty/${parentFolderId}/` : `/dokumenty`
        );
      }
    } else {
      navigate(parentFolderId ? `/dokumenty/${parentFolderId}/` : `/dokumenty`);
    }

    return () => {};
  }, [folderId, documentData.moveSelectedDocuments]);

  useEffect(() => {
    setTimeout(fillMissingTags(), 1000);
    return () => {};
  }, []);

  const handleBreadcrumbsItemClick = (event, nodeId) => {
    event.preventDefault();
    setFolderId(nodeId);
  };

  const onSelectedDocumentIdsChanged = (viewSelectedDocumentIds) => {
    if (viewSelectedDocumentIds?.length > 0) {
      let selDocument = documents.filter(
        (doc) => doc.id === viewSelectedDocumentIds[0]
      );

      if (selDocument?.length === 1) {
        // move published to draft catalog or move not public access to public access folder or node is document - don't make sense
        if (
          (firstStatus === "published" && selDocument[0].status === "draft") ||
          (firstPublicAccess && !selDocument[0].publicAccess) ||
          selDocument[0].nodeType === "2"
        ) {
          setChildMoveAvailable(false);
          setValue("position","afterdoc");
        } else {
          setChildMoveAvailable(true);
        }
      } else {
        setChildMoveAvailable(true);
      }
    } else {
      setChildMoveAvailable(true);
    }

    setSelectedDocumentIds(viewSelectedDocumentIds);
  };

  const getSelectedToMoveIds = () => {
    if (documentData.moveSelectedDocuments) {
      return documentData.moveSelectedDocuments.map((doc) => {
        return doc.id;
      });
    }
    return null;
  };

  const internalOnSubmit = (values) => {
    // console.log("values: " + values);
    // console.log("selectedDocumentIds: " + selectedDocumentIds);

    enableGlobalLoader();

    let nodeIds = getSelectedToMoveIds();
    // console.log("nodeIds: " + nodeIds);

    if (selectedDocumentIds?.length > 0) {
      apiPostRequest(
        apiEndpoints.structMove.path,
        {
          parentId: selectedDocumentIds[0],
          siblingId: selectedDocumentIds[0],
          position: values.position,
          nodeIds: nodeIds,
        },
        true
      )
        .then((data) => {
          displaySnackbar({
            isOpen: true,
            severity: "success",
            autoHideDuration: 3000,
            message: "Katalogi/dokumenty zostały przeniesione",
          });

          if (parentFolderId) {
            navigate("/dokumenty/" + parentFolderId);
          } else {
            navigate("/dokumenty");
          }

          disableGlobalLoader();
        })
        .catch((error) => {
          disableGlobalLoader();

          if (error.response && error.response.status === 401) {
            setLogoutUser();
          } else {
            handleDocumentError(error);
          }
        });
    } else {
      disableGlobalLoader();

      displaySnackbar({
        isOpen: true,
        severity: "error",
        autoHideDuration: 3000,
        message: "Nie wybrano lokalizacji docelowej",
      });
    }
  };

  const handleDocumentError = (error) => {
    if (
      error.response.status === 403 &&
      error.response.data &&
      error.response.data.code === "ERROR_NOT_PUBLIC_ACCESS_PARENT"
    ) {
      displaySnackbar({
        isOpen: true,
        severity: "error",
        autoHideDuration: 8000,
        message:
          "Brak niezbędnych uprawnień aby przenieść katalogi/dokumenty do wskazanej lokalizacji.",
      });
    } else {
      displaySnackbar({
        isOpen: true,
        severity: "error",
        autoHideDuration: 8000,
        message: kmMessages.error.global.genericerror,
      });
    }
  };

  return (
    <Card className={clsx(classes.root)}>
      <Box component="main">
        <Grid container className={classes.root}>
          <Grid item xs={12} elevation={2}>
            <form onSubmit={handleSubmit(internalOnSubmit)}>
              <FormControl fullWidth className={classes.formcontrol}>
                <Box className={classes.paddingbottom}>
                  <FormLabel>Katalogi/dokumenty przenoszone:</FormLabel>
                </Box>

                <KmFilteredDocumentsTable
                  levelDetails={{}}
                  markable={false}
                  searchable={false}
                  headCells={headCellsToMove}
                  rows={
                    documentData.moveSelectedDocuments
                      ? documentData.moveSelectedDocuments
                      : []
                  }
                  defaultOrder="asc"
                  disableAttendanceChecking={true}
                />
              </FormControl>

              

              <FormControl fullWidth className={classes.formcontrol}>
                <Box className={classes.paddingbottom}>
                  <FormLabel>Lokalizacja docelowa:</FormLabel>
                </Box>

                <KmFilteredDocumentsTable
                  levelDetails={levelDetails}
                  markable={true}
                  allSelectable={false}
                  oneSelectRows={true}
                  searchable={false}
                  headCells={headCells}
                  rows={documents}
                  defaultOrder="asc"
                  onBreadcrumbsItemClick={handleBreadcrumbsItemClick}
                  onSelectedRowIdsChange={onSelectedDocumentIdsChanged}
                  disableAttendanceChecking={true}
                />
              </FormControl>

              <FormControl
                component="fieldset"
                className={classes.formcontrol}
                fullWidth
              >
                <FormLabel component="legend">Pozycja w katalogu:</FormLabel>
                <Controller
                  as={
                    <RadioGroup row aria-label="position" name="position">
                      <FormControlLabel
                        value="afterdoc"
                        control={<Radio color="primary" />}
                        label="za wskazanym"
                      />
                      <FormControlLabel
                        value="beforedoc"
                        control={<Radio color="primary" />}
                        label="przed wskazanym"
                      />
                      {childMoveAvailable ? (
                        <>
                          <FormControlLabel
                            value="start"
                            control={<Radio color="primary" />}
                            label="pierwszy potomny wskazanego"
                          />
                          <FormControlLabel
                            value="end"
                            control={<Radio color="primary" />}
                            label="ostatni potomny wskazanego"
                          />
                        </>
                      ) : (
                        <></>
                      )}
                    </RadioGroup>
                  }
                  name="position"
                  defaultValue="afterdoc"
                  control={control}
                  rules={{ required: true }}
                />
              </FormControl>

              <Box className={classes.paddingtop}>
                <Button
                  color="primary"
                  className={classes.button}
                  component={RouterLink}
                  to={
                    parentFolderId
                      ? `/dokumenty/${parentFolderId}/`
                      : `/dokumenty`
                  }
                >
                  Anuluj
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  className={classes.button}
                  endIcon={<SaveIcon />}
                  type="submit"
                >
                  Zapisz
                </Button>
              </Box>
            </form>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};

const mapStateToProps = (state) => ({
  documentData: state.documentData,
});

const mapDispatchToProps = (dispatch) => ({
  setLogoutUser: () => dispatch(actions.setLogoutUser()),
  enableGlobalLoader: () => dispatch(actions.enableGlobalLoader()),
  disableGlobalLoader: () => dispatch(actions.disableGlobalLoader()),
  displaySnackbar: (snackBarParameters) =>
    dispatch(actions.displaySnackbar(snackBarParameters)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MoveForm);
